import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { Hash, CreditCard } from 'lucide-react';
import apiClient from '@/services/api';
import type { PaymentsReport } from '@/types/api';
import { KPICard } from '@/components/reports/KPICard';
import { ReportFrame, SimpleTable } from '@/components/reports/ReportFrame';
import type { ReportViewProps } from '@/components/reports/reportTypes';
import { paymentClass } from '@/components/reports/paymentClass';
import { buildGenericCsv, downloadCsv, printGenericReport, type ReportSection } from '@/utils/reportExport';

/** Credit Card: card payment transactions, mirroring the POS Credit Card report. */
export default function CreditCardReport({ filters }: ReportViewProps) {
  const { selectedRestaurantId, startDate, endDate, formatCurrency, selectedRestaurant } = filters;

  const { data, isLoading, isError, refetch } = useQuery<PaymentsReport>({
    queryKey: ['payments-report', selectedRestaurantId, startDate, endDate],
    queryFn: () => apiClient.getPaymentsReport(selectedRestaurantId!, startDate, endDate),
    enabled: !!selectedRestaurantId,
  });

  const cardTxns = (data?.transactions ?? []).filter((x) => paymentClass(x.paymentType, x.cardType) === 'card');
  const grossTotal = cardTxns.reduce((a, x) => a + Number(x.amount), 0);
  const salesCount = cardTxns.length;

  const meta = () => ({ restaurantName: selectedRestaurant?.name ?? '', startDate, endDate, formatCurrency });
  const fmtDate = (s: string | null) => (s ? format(parseISO(s), 'dd-MM-yyyy') : '—');

  const headers = ['Ticket ID', 'Card Type', 'Date', 'Server', 'Auth Code', 'Total'];
  const rows = () => cardTxns.map((x) => [
    x.posTicketId ?? '—',
    x.cardType ?? '—',
    fmtDate(x.processedAt),
    x.serverName ?? '—',
    x.authorizationCode ?? '—',
    formatCurrency(x.amount),
  ]);

  const sections = (): ReportSection[] => [{ heading: 'Transactions', headers, rows: rows() }];
  const kpis = (): [string, string][] => [
    ['Total Sales Count', salesCount.toLocaleString()],
    ['Total', formatCurrency(grossTotal)],
  ];

  return (
    <ReportFrame
      title="Credit Card"
      description="Card payment transactions and totals."
      hasRestaurant={!!selectedRestaurantId}
      isLoading={isLoading}
      isError={isError}
      onRetry={() => refetch()}
      isEmpty={cardTxns.length === 0}
      exportDisabled={!data}
      onExportCsv={() => data && downloadCsv(`credit-card-${format(new Date(), 'yyyy-MM-dd')}.csv`, buildGenericCsv('Credit Card', meta(), kpis(), sections()))}
      onExportPdf={() => data && printGenericReport('Credit Card Report', meta(), kpis(), sections())}
    >
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <KPICard label="Total Sales Count" value={salesCount.toLocaleString()} icon={Hash} palette="purple" />
        <KPICard label="Total" value={formatCurrency(grossTotal)} icon={CreditCard} palette="red" />
      </div>
      <div className="card"><div className="card-body">
        <h3 className="text-lg font-semibold mb-4">Transactions{data?.transactionsTruncated ? ' (capped at 2000)' : ''}</h3>
        <SimpleTable headers={headers} rows={rows()} />
      </div></div>
    </ReportFrame>
  );
}
